// src/pages/lead/LeadRecruitment.jsx
import React, { useState } from "react";
import { LEAD_NAV } from "../../utils/navigation";
import Layout from "../../components/common/Layout";
import { useMyRecruitmentRequests, createRecruitmentRequest } from "../../hooks/useRecruitment";
import { useAuth } from "../../context/AuthContext";

export default function LeadRecruitment() {
  const { userProfile } = useAuth();
  const uid = userProfile?.uid || userProfile?.id;
  const { data: myRequests, loading } = useMyRecruitmentRequests(uid);

  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    positionTitle: "",
    department: userProfile?.department || "Engineering",
    openings: 1,
    skills: "",
    experience: "2-4 years",
    employmentType: "Full-Time",
    priority: "Medium",
    expectedJoiningDate: "",
    salaryRange: "8 - 12 LPA",
    justification: "",
  });

  async function handleSubmit(e) {
    e.preventDefault();
    await createRecruitmentRequest({
      ...formData,
      openings: Number(formData.openings),
      skills: typeof formData.skills === "string" ? formData.skills.split(",").map((s) => s.trim()) : formData.skills,
      requestedById: uid,
      requestedByName: userProfile?.displayName || "Team Lead",
    });
    setShowModal(false);
    alert("Hiring request submitted to HR for approval!");
  }

  return (
    <Layout navItems={LEAD_NAV} panelLabel="Team Lead Panel">
      <style>{`
        .lead-req-card { background: var(--bg-card, #fff); border: 1px solid var(--border, #e5e7eb); border-radius: 12px; padding: 1.25rem; box-shadow: 0 1px 5px rgba(0,0,0,0.05); margin-bottom: 1rem; }
        .req-status-pill { padding: 0.2rem 0.65rem; border-radius: 99px; font-size: 0.72rem; font-weight: 700; }
        .req-status--Submitted { background: #fef3c7; color: #92400e; }
        .req-status--Approved { background: #d1fae5; color: #065f46; }
        .req-status--Rejected { background: #fee2e2; color: #991b1b; }
      `}</style>

      <div className="page-header">
        <div>
          <h1 className="page-title">Team Hiring Requisitions</h1>
          <p className="page-subtitle">Request new headcount and track HR approval status for your team</p>
        </div>
        <button className="btn-primary" onClick={() => setShowModal(true)}>
          + Submit Hiring Request
        </button>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: "3rem", color: "var(--text-muted)" }}>Loading your requests...</div>
      ) : myRequests.length === 0 ? (
        <div style={{ background: "var(--bg-card, #fff)", border: "1px dashed var(--border)", borderRadius: 12, padding: "3rem", textAlign: "center", color: "var(--text-muted)" }}>
          No hiring requisitions submitted yet. Click "+ Submit Hiring Request" to request new team members.
        </div>
      ) : (
        <div>
          {myRequests.map((req) => (
            <div key={req.id} className="lead-req-card">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div>
                  <div style={{ fontSize: "1.05rem", fontWeight: 700 }}>{req.positionTitle}</div>
                  <div style={{ fontSize: "0.78rem", color: "var(--text-muted)", marginTop: 4 }}>
                    {req.department} · {req.openings} opening(s) · Priority: {req.priority}
                  </div>
                </div>
                <span className={`req-status-pill req-status--${req.status}`}>{req.status}</span>
              </div>
              <div style={{ marginTop: "0.75rem", fontSize: "0.82rem", background: "var(--bg-secondary)", padding: "0.75rem", borderRadius: 8 }}>
                <strong>Justification:</strong> {req.justification || "—"}
              </div>
              {req.rejectionReason && (
                <div style={{ marginTop: "0.5rem", color: "#ef4444", fontSize: "0.78rem", fontWeight: 600 }}>
                  Rejection Reason: {req.rejectionReason}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="modal-overlay">
          <div className="modal-body" style={{ maxWidth: 550 }}>
            <h3 style={{ margin: "0 0 1rem", fontWeight: 700 }}>Request New Team Hiring</h3>
            <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "0.85rem" }}>
              <div>
                <label style={{ fontSize: "0.78rem", fontWeight: 600 }}>Position Title *</label>
                <input
                  type="text"
                  className="ats-input"
                  style={{ width: "100%" }}
                  required
                  placeholder="e.g. Senior Frontend Engineer"
                  value={formData.positionTitle}
                  onChange={(e) => setFormData({ ...formData, positionTitle: e.target.value })}
                />
              </div>

              <div className="form-grid-2">
                <div>
                  <label style={{ fontSize: "0.78rem", fontWeight: 600 }}>Department</label>
                  <input
                    type="text"
                    className="ats-input"
                    style={{ width: "100%" }}
                    value={formData.department}
                    onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                  />
                </div>
                <div>
                  <label style={{ fontSize: "0.78rem", fontWeight: 600 }}>Number of Openings</label>
                  <input
                    type="number"
                    min="1"
                    className="ats-input"
                    style={{ width: "100%" }}
                    value={formData.openings}
                    onChange={(e) => setFormData({ ...formData, openings: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-grid-2">
                <div>
                  <label style={{ fontSize: "0.78rem", fontWeight: 600 }}>Experience Required</label>
                  <input
                    type="text"
                    className="ats-input"
                    style={{ width: "100%" }}
                    placeholder="e.g. 3-5 years"
                    value={formData.experience}
                    onChange={(e) => setFormData({ ...formData, experience: e.target.value })}
                  />
                </div>
                <div>
                  <label style={{ fontSize: "0.78rem", fontWeight: 600 }}>Priority</label>
                  <select
                    className="ats-select"
                    style={{ width: "100%" }}
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                  >
                    <option value="Low">Low</option>
                    <option value="Medium">Medium</option>
                    <option value="High">High</option>
                    <option value="Urgent">Urgent</option>
                  </select>
                </div>
              </div>

              <div>
                <label style={{ fontSize: "0.78rem", fontWeight: 600 }}>Required Skills (comma separated)</label>
                <input
                  type="text"
                  className="ats-input"
                  style={{ width: "100%" }}
                  placeholder="React, TypeScript, Redux"
                  value={formData.skills}
                  onChange={(e) => setFormData({ ...formData, skills: e.target.value })}
                />
              </div>

              <div>
                <label style={{ fontSize: "0.78rem", fontWeight: 600 }}>Business Justification *</label>
                <textarea
                  className="ats-input"
                  style={{ width: "100%", height: 80, resize: "vertical" }}
                  required
                  placeholder="Why is this role needed? (e.g., Team expansion for Project X)"
                  value={formData.justification}
                  onChange={(e) => setFormData({ ...formData, justification: e.target.value })}
                />
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem", marginTop: "0.5rem" }}>
                <button type="button" className="btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn-primary">Submit to HR</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}
